import { useMutation, useQueryClient } from '@tanstack/react-query';

import { fetcher } from '#lib/backendFetcher';

import {
	generateAICampaign,
	regenerateEmail,
	generatePendingEmails,
	cancelAICampaignGeneration,
} from '../../api/aiCampaignApi';
import {
	upsertAIConfig,
	updateGeneratedEmail,
	approveGeneratedEmail,
	approveAllGeneratedEmails,
	unapproveGeneratedEmail,
} from '../../api/emailCampaignsApi';
import { emailCampaignsKeys } from '../emailCampaigns/keys';

import { aiCampaignKeys } from './keys';

/**
 * Hook to start AI email generation
 * Uses backend endpoint for OpenAI integration and job queue
 */
export const useGenerateAICampaign = (campaignId) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: () => generateAICampaign(campaignId),
		onSuccess: async () => {
			// Clear emails cache since backend deletes all existing emails in full mode
			queryClient.removeQueries({ queryKey: aiCampaignKeys.emailsAll(campaignId) });
			// Force an immediate status refetch so the UI reliably picks up status: 'generating'
			await queryClient.refetchQueries({ queryKey: aiCampaignKeys.status(campaignId) });
		},
	});
};

/**
 * Hook to update a generated email
 */
export const useUpdateGeneratedEmail = (campaignId) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ emailId, subject, content }) => {
			const result = await updateGeneratedEmail(emailId, { subject, content, campaignId });
			if (result.error) throw result.error;
			return result;
		},
		onSuccess: () => {
			// Invalidate emails list and status for this campaign
			queryClient.invalidateQueries({ queryKey: aiCampaignKeys.emailsAll(campaignId) });
			queryClient.invalidateQueries({ queryKey: aiCampaignKeys.status(campaignId) });
		},
	});
};

/**
 * Hook to approve a single email
 * If campaign is active, automatically schedules the approved email
 */
export const useApproveEmail = (campaignId) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ emailId, campaignStatus }) => {
			const result = await approveGeneratedEmail(emailId, { campaignId });
			if (result.error) throw result.error;

			// Auto-schedule if campaign is active
			if (campaignStatus === 'active') {
				await fetcher(`/api/v1/email/campaigns/${campaignId}/manage`, {
					method: 'POST',
					body: { action: 'start' },
				});
			}

			return result;
		},
		onSuccess: () => {
			// Invalidate emails list and status for this campaign
			queryClient.invalidateQueries({ queryKey: aiCampaignKeys.emailsAll(campaignId) });
			queryClient.invalidateQueries({ queryKey: aiCampaignKeys.status(campaignId) });
		},
	});
};

/**
 * Hook to revert an approved email back to pending review
 * If the campaign is active, the backend cancels the already-scheduled send
 */
export const useUnapproveEmail = (campaignId) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ emailId }) => {
			const result = await unapproveGeneratedEmail(emailId, { campaignId });
			if (result.error) throw result.error;
			return result;
		},
		onSuccess: () => {
			// Invalidate emails list and status for this campaign
			queryClient.invalidateQueries({ queryKey: aiCampaignKeys.emailsAll(campaignId) });
			queryClient.invalidateQueries({ queryKey: aiCampaignKeys.status(campaignId) });
		},
	});
};

/**
 * Hook to approve all emails
 * If campaign is active, automatically schedules all approved emails
 */
export const useApproveAllEmails = (campaignId) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (campaignStatus) => {
			const result = await approveAllGeneratedEmails(campaignId);
			if (result.error) throw result.error;

			// Auto-schedule if campaign is active
			if (campaignStatus === 'active') {
				await fetcher(`/api/v1/email/campaigns/${campaignId}/manage`, {
					method: 'POST',
					body: { action: 'start' },
				});
			}

			return result;
		},
		onSuccess: () => {
			// Invalidate emails list and status for this campaign
			queryClient.invalidateQueries({ queryKey: aiCampaignKeys.emailsAll(campaignId) });
			queryClient.invalidateQueries({ queryKey: aiCampaignKeys.status(campaignId) });
		},
	});
};

/**
 * Hook to regenerate a single email
 * Uses backend endpoint for OpenAI integration
 */
export const useRegenerateEmail = (campaignId) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (emailId) => regenerateEmail(emailId),
		onSuccess: () => {
			// Invalidate emails list and status for this campaign
			queryClient.invalidateQueries({ queryKey: aiCampaignKeys.emailsAll(campaignId) });
			queryClient.invalidateQueries({ queryKey: aiCampaignKeys.status(campaignId) });
		},
	});
};

/**
 * Hook to update AI configuration
 */
export const useUpdateAIConfig = (campaignId) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (config) => {
			const result = await upsertAIConfig(campaignId, config);
			if (result.error) throw result.error;
			return result;
		},
		onSuccess: () => {
			// Invalidate campaign detail to refresh AI config
			queryClient.invalidateQueries({ queryKey: emailCampaignsKeys.detail(campaignId) });
		},
	});
};

/**
 * Hook to generate emails for pending contacts only (incremental generation)
 * Uses backend endpoint for OpenAI integration and job queue
 */
export const useGeneratePendingEmails = (campaignId) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: () => generatePendingEmails(campaignId),
		onSuccess: async () => {
			queryClient.invalidateQueries({ queryKey: aiCampaignKeys.emailsAll(campaignId) });
			queryClient.invalidateQueries({ queryKey: aiCampaignKeys.pendingContacts(campaignId) });
			// Force an immediate status refetch so the UI reliably picks up status: 'generating'
			// and the progress UI / polling start without waiting for a later trigger.
			await queryClient.refetchQueries({ queryKey: aiCampaignKeys.status(campaignId) });
		},
	});
};

/**
 * Hook to cancel an ongoing AI email generation
 * Already generated emails are preserved
 */
export const useCancelAICampaignGeneration = (campaignId) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: () => cancelAICampaignGeneration(campaignId),
		onSuccess: () => {
			// Invalidate queries to refetch fresh data and stop polling
			queryClient.invalidateQueries({ queryKey: aiCampaignKeys.status(campaignId) });
			queryClient.invalidateQueries({ queryKey: aiCampaignKeys.emailsAll(campaignId) });
			queryClient.invalidateQueries({ queryKey: aiCampaignKeys.pendingContacts(campaignId) });
		},
	});
};
